/**
 * @file GameApplication.ts
 * @role Thin wrapper around the Pixi Application: initializes the renderer, mounts the
 *       canvas, and exposes stage + ticker. Keeps Pixi setup in one place.
 * @owns The Pixi Application instance and its canvas mount.
 * @reads A host HTMLElement to mount into.
 * @neverDoes Run game logic. Read raw frame delta (that is GameTicker's job).
 * @invariants 22 (the ticker is consumed only through GameTicker).
 * @see docs/ARCHITECTURE.md
 */
import {
  Application,
  type AccessibilitySystemOptions,
  type ApplicationOptions,
  type Container,
  type Texture,
  type Ticker,
} from 'pixi.js';
import type { LayoutContext } from '@core/layout/LayoutContext';
import type { LayoutService } from '@core/layout/LayoutService';

export class GameApplication {
  readonly pixi = new Application();
  private started = false;
  private offLayout: (() => void) | undefined;
  private layout: LayoutService | undefined;
  private maxDevicePixelRatio = 2;
  private accessibilityKeydown: ((event: KeyboardEvent) => void) | undefined;

  async init(host: HTMLElement, antialias = true): Promise<void> {
    this.detachAccessibilityKeydown();
    const options: Partial<ApplicationOptions> & AccessibilitySystemOptions = {
      width: 1,
      height: 1,
      background: 0x0a0a12,
      antialias,
      autoDensity: true,
      resolution: 1,
      preference: 'webgl',
      skipExtensionImports: true,
      accessibilityOptions: {
        // Pixi 8.19 activates and then detaches its post-render updater during init:
        // https://github.com/pixijs/pixijs/blob/v8.19.0/src/accessibility/AccessibilitySystem.ts#L444-L450
        // Delay keyboard activation until after init; the exact dependency is pinned and tested
        // so this workaround must be revalidated before a Pixi upgrade can land.
        enabledByDefault: false,
        activateOnTab: false,
        deactivateOnMouseMove: false,
      },
    };
    await this.pixi.init(options);
    host.appendChild(this.pixi.canvas);
    const accessibilityKeydown = (event: KeyboardEvent): void => {
      if (event.key !== 'Tab') return;
      const { accessibility } = this.pixi.renderer;
      accessibility.setAccessibilityEnabled(true);
      // Render once through Application's public API so the newly enabled DOM overlay is
      // synchronized immediately without reaching into renderer runners.
      this.pixi.render();
      globalThis.removeEventListener('keydown', accessibilityKeydown);
      if (this.accessibilityKeydown === accessibilityKeydown) {
        this.accessibilityKeydown = undefined;
      }
    };
    this.accessibilityKeydown = accessibilityKeydown;
    globalThis.addEventListener('keydown', accessibilityKeydown);
    this.started = true;
  }

  /** Bind renderer sizing to the same coalesced layout stream used by scenes. */
  connectLayout(layout: LayoutService, maxDevicePixelRatio = 2): void {
    this.offLayout?.();
    this.layout = layout;
    this.maxDevicePixelRatio = maxDevicePixelRatio;
    const apply = (context: LayoutContext): void => {
      if (!this.started) return;
      const resolution = Math.min(this.maxDevicePixelRatio, context.devicePixelRatio);
      this.pixi.renderer.resize(context.width, context.height, resolution);
    };
    this.offLayout = layout.subscribe(apply);
    apply(layout.context);
  }

  setMaxDevicePixelRatio(maxDevicePixelRatio: number): void {
    this.maxDevicePixelRatio = Math.max(0.5, maxDevicePixelRatio);
    const context = this.layout?.context;
    if (!this.started || !context) return;
    this.pixi.renderer.resize(
      context.width,
      context.height,
      Math.min(this.maxDevicePixelRatio, context.devicePixelRatio),
    );
  }

  setMaxFps(maxFps: number): void {
    this.pixi.ticker.maxFPS = Math.max(15, maxFps);
  }

  get stage(): Container {
    return this.pixi.stage;
  }

  /** Raw Pixi ticker — consume ONLY via GameTicker, never directly for physics. */
  get ticker(): Ticker {
    return this.pixi.ticker;
  }

  /** Upload decoded critical textures before the first gameplay frame can demand them. */
  prepareTextures(textures: readonly Texture[]): Promise<void> {
    for (const texture of textures) {
      this.pixi.renderer.texture.initSource(texture.source);
    }
    return Promise.resolve();
  }

  destroy(): void {
    this.detachAccessibilityKeydown();
    this.offLayout?.();
    this.offLayout = undefined;
    this.layout = undefined;
    if (this.started) {
      this.pixi.destroy(true, { children: true });
      this.started = false;
    }
  }

  private detachAccessibilityKeydown(): void {
    const handler = this.accessibilityKeydown;
    if (!handler) return;
    globalThis.removeEventListener('keydown', handler);
    this.accessibilityKeydown = undefined;
  }
}
